import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe,
  Search,
  CheckCircle2,
  FileSpreadsheet,
  UploadCloud,
  Code2,
  Copy,
  Check,
  ExternalLink,
  Barcode,
  Layers,
  Sparkles,
  RefreshCw,
  Eye,
  Smartphone,
  Monitor,
} from 'lucide-react';
import {
  PageHeader,
  Panel,
  PanelHeader,
  PanelBody,
  Button,
  Badge,
  Input,
  Select,
  Table,
  TableShell,
  THead,
  TBody,
  Tr,
  Th,
  Td,
  cn,
} from '../ui/index.js';
import {
  fetchSeoAuditSummary,
  fetchSeoProducts,
  fetchProductSchema,
  pushSeoAltsWoo,
  type SeoProductItem,
} from '../api.js';

type TabKey = 'overview' | 'media' | 'ean' | 'schema' | 'content';

export function SeoAuditPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [deficiencyFilter, setDeficiencyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score_asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProductForModal, setSelectedProductForModal] = useState<SeoProductItem | null>(null);
  const [copiedSchema, setCopiedSchema] = useState(false);
  const [serpViewMode, setSerpViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [toastMsg, setToastMsg] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);

  // Queries
  const { data: summary, isLoading: isSummaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['seo-audit-summary'],
    queryFn: fetchSeoAuditSummary,
    staleTime: 60_000,
  });

  const { data: productsData, isLoading: isProductsLoading, isFetching: isProductsFetching } = useQuery({
    queryKey: ['seo-products', currentPage, deficiencyFilter, searchQuery, sortBy],
    queryFn: () =>
      fetchSeoProducts({
        page: currentPage,
        limit: 25,
        deficiency: deficiencyFilter,
        search: searchQuery.trim() || undefined,
        sortBy,
      }),
    staleTime: 15_000,
  });

  const { data: schemaData, isLoading: isSchemaLoading } = useQuery({
    queryKey: ['seo-product-schema', selectedProductId],
    queryFn: () => (selectedProductId ? fetchProductSchema(selectedProductId) : null),
    enabled: selectedProductId !== null,
  });

  // Mutations
  const pushAltsMutation = useMutation({
    mutationFn: (items: { productId: number; wcProductId: number; altText: string }[]) =>
      pushSeoAltsWoo(items),
    onSuccess: (data) => {
      setToastMsg({
        text: `Textes alternatifs synchronisés avec succès pour ${data.updatedCount} produit(s) sur WooCommerce.`,
        tone: 'ok',
      });
      void queryClient.invalidateQueries({ queryKey: ['seo-products'] });
      void queryClient.invalidateQueries({ queryKey: ['seo-audit-summary'] });
    },
    onError: (err) => {
      setToastMsg({
        text: `Erreur de synchronisation : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'error',
      });
    },
  });

  const handleCopySchema = () => {
    if (schemaData?.jsonString) {
      void navigator.clipboard.writeText(schemaData.jsonString);
      setCopiedSchema(true);
      setTimeout(() => setCopiedSchema(false), 2000);
    }
  };

  const handlePushSingleAlt = (p: SeoProductItem) => {
    pushAltsMutation.mutate([
      {
        productId: p.id,
        wcProductId: p.wcProductId ?? p.id,
        altText: p.seo.suggestedAlt,
      },
    ]);
  };

  const handleExportCsv = () => {
    window.open('/api/v1/seo/export-audit', '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Toast Alert */}
      {toastMsg && (
        <div
          className={cn(
            'p-3.5 rounded-xl text-xs font-semibold flex items-center justify-between border shadow-sm transition-all',
            toastMsg.tone === 'ok'
              ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
              : 'bg-rose-50 text-rose-900 border-rose-200',
          )}
        >
          <span>{toastMsg.text}</span>
          <button
            type="button"
            onClick={() => setToastMsg(null)}
            className="text-ink-faint hover:text-ink text-xs font-bold px-2 py-0.5"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Page Header */}
      <PageHeader
        index="S34"
        title="Audit & Optimisation SEO"
        purpose="Centre de contrôle du référencement naturel et Google Shopping : photos haute résolution ≥ 1000px, 4 attributs médias (Alt/Légende/Desc), EAN-13, balisage Schema.org JSON-LD et titres optimisés."
        meta={[
          { label: 'Score SEO Moyen', value: summary ? `${summary.averageScore} / 100` : '…' },
          { label: 'Avec EAN-13', value: summary ? `${summary.withBarcodeCount} (${Math.round((summary.withBarcodeCount / summary.totalEligible) * 100)}%)` : '…' },
          { label: 'Avec Photos', value: summary ? `${summary.withImagesCount} (${Math.round((summary.withImagesCount / summary.totalEligible) * 100)}%)` : '…' },
          { label: 'Prêts Shopping', value: summary ? `${summary.readyShoppingEstimated}` : '…' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={() => refetchSummary()}
              disabled={isSummaryLoading}
            >
              <RefreshCw className={cn('w-4 h-4 mr-1.5', isSummaryLoading && 'animate-spin')} />
              <span>Actualiser</span>
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={handleExportCsv}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-semibold shadow-sm"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              <span>Exporter Audit SEO (CSV)</span>
            </Button>
          </div>
        }
      />

      {/* Navigation Sub-Tabs */}
      <div className="flex items-center gap-1 border-b border-border bg-card/60 p-1.5 rounded-xl shadow-2xs overflow-x-auto">
        <button
          type="button"
          onClick={() => setActiveTab('overview')}
          className={cn(
            'px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors whitespace-nowrap',
            activeTab === 'overview'
              ? 'bg-ink text-canvas shadow-xs'
              : 'text-ink-soft hover:text-ink hover:bg-cream/70',
          )}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>Vue d'ensemble & Score Santé</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('media')}
          className={cn(
            'px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors whitespace-nowrap',
            activeTab === 'media'
              ? 'bg-ink text-canvas shadow-xs'
              : 'text-ink-soft hover:text-ink hover:bg-cream/70',
          )}
        >
          <UploadCloud className="w-3.5 h-3.5" />
          <span>Photos & Médias (1000px & Alts)</span>
          {summary && summary.withoutImagesCount > 0 && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-rose-500 text-white font-bold">
              {summary.withoutImagesCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('ean')}
          className={cn(
            'px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors whitespace-nowrap',
            activeTab === 'ean'
              ? 'bg-ink text-canvas shadow-xs'
              : 'text-ink-soft hover:text-ink hover:bg-cream/70',
          )}
        >
          <Barcode className="w-3.5 h-3.5" />
          <span>Identifiants EAN-13 & Shopping</span>
          {summary && summary.withoutBarcodeCount > 0 && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-amber-500 text-white font-bold">
              {summary.withoutBarcodeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('schema')}
          className={cn(
            'px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors whitespace-nowrap',
            activeTab === 'schema'
              ? 'bg-ink text-canvas shadow-xs'
              : 'text-ink-soft hover:text-ink hover:bg-cream/70',
          )}
        >
          <Code2 className="w-3.5 h-3.5" />
          <span>Simulateur Schema.org JSON-LD</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('content')}
          className={cn(
            'px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors whitespace-nowrap',
            activeTab === 'content'
              ? 'bg-ink text-canvas shadow-xs'
              : 'text-ink-soft hover:text-ink hover:bg-cream/70',
          )}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>Titres, Métas & SERP Google</span>
        </button>
      </div>

      {/* TAB 1: OVERVIEW & HEALTH SCORE */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Main Hero Score Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm relative overflow-hidden">
              <div className="text-xs font-mono uppercase tracking-wider text-ink-faint">Score SEO Moyen</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold font-mono text-ink">
                  {summary ? summary.averageScore : '…'}
                </span>
                <span className="text-xs font-mono text-ink-soft">/ 100</span>
              </div>
              <div className="mt-3 w-full bg-cream rounded-full h-2 overflow-hidden">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${summary ? summary.averageScore : 0}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-ink-faint font-mono">
                <span>Échantillon analysé : {summary?.sampleAnalyzed ?? 0}</span>
                <span>Barème Google E-com</span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="text-xs font-mono uppercase tracking-wider text-ink-faint">Couverture EAN-13</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold font-mono text-emerald-600">
                  {summary ? summary.withBarcodeCount : '…'}
                </span>
                <span className="text-xs font-mono text-ink-soft">
                  / {summary?.totalEligible ?? '…'}
                </span>
              </div>
              <div className="mt-3 text-xs text-ink-soft flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                <span>
                  {summary ? Math.round((summary.withBarcodeCount / summary.totalEligible) * 100) : 0}% des articles ont un EAN
                </span>
              </div>
              <div className="mt-1 text-[11px] text-amber-700 font-mono">
                {summary?.withoutBarcodeCount ?? 0} articles sans code-barres
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="text-xs font-mono uppercase tracking-wider text-ink-faint">Couverture Photos</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold font-mono text-sky-600">
                  {summary ? summary.withImagesCount : '…'}
                </span>
                <span className="text-xs font-mono text-ink-soft">
                  / {summary?.totalEligible ?? '…'}
                </span>
              </div>
              <div className="mt-3 text-xs text-ink-soft flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-sky-600" />
                <span>
                  {summary ? Math.round((summary.withImagesCount / summary.totalEligible) * 100) : 0}% des fiches ont un visuel
                </span>
              </div>
              <div className="mt-1 text-[11px] text-rose-700 font-mono">
                {summary?.withoutImagesCount ?? 0} articles sans aucune image
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="text-xs font-mono uppercase tracking-wider text-ink-faint">Google Free Shopping</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold font-mono text-herb-700">
                  {summary ? summary.readyShoppingEstimated : '…'}
                </span>
                <span className="text-xs font-mono text-ink-soft">prêts</span>
              </div>
              <div className="mt-3 text-xs text-ink-soft flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-herb-700" />
                <span>Critères complets : GTIN + Image + Prix + Stock</span>
              </div>
              <div className="mt-1 text-[11px] text-ink-faint font-mono">
                Éligibilité flux marchand direct
              </div>
            </div>
          </div>

          {/* Les 6 Piliers SEO e-Commerce Banner */}
          <Panel>
            <PanelHeader
              index="BASES"
              title="Les 6 Bases Indispensables du SEO E-Commerce (Checklist Mapara)"
              hint="Exigences formelles des moteurs de recherche et de Google Merchant Center"
            />
            <PanelBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-emerald-100 text-emerald-800 text-xs font-mono font-bold">01</span>
                    <h4 className="text-xs font-bold text-ink">EAN-13 / GTIN Obligatoire</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Le code-barres 13 chiffres est la clé primaire mondiale de Google. Sans GTIN, Google Shopping rejette les produits et refuse l'affichage des Rich Snippets dans la recherche gratuite.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Norme GS1 validée</Badge>
                  </div>
                </div>

                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-sky-100 text-sky-800 text-xs font-mono font-bold">02</span>
                    <h4 className="text-xs font-bold text-ink">Photos 1000x1000px & Format 1:1</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Résolution minimale de 1000x1000px requise pour permettre le zoom haute définition sans pixellisation. Format carré évitant les coupures asymétriques sur smartphone.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Standard Google Images</Badge>
                  </div>
                </div>

                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-amber-100 text-amber-800 text-xs font-mono font-bold">03</span>
                    <h4 className="text-xs font-bold text-ink">4 Métadonnées Médias Complètes</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Chaque image produit doit renseigner : <strong>Alt Text</strong> (texte alternatif indexé), <strong>Légende</strong> (Caption), <strong>Description</strong> et <strong>Titre média</strong> propre.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Accessibilité WCAG</Badge>
                  </div>
                </div>

                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-indigo-100 text-indigo-800 text-xs font-mono font-bold">04</span>
                    <h4 className="text-xs font-bold text-ink">Schema.org JSON-LD Valide</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Données structurées microdonnées injectées dans le code source de chaque fiche produit : balisage <code>Product</code>, <code>Offer</code>, <code>Brand</code>, <code>InStock</code>, prix en TND.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Rich Snippets Google</Badge>
                  </div>
                </div>

                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-rose-100 text-rose-800 text-xs font-mono font-bold">05</span>
                    <h4 className="text-xs font-bold text-ink">Titres Calibrés (35 - 65 car.)</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Structure canonique : <code>[Marque] + [Nom Produit] + [Contenance]</code>. Évite la troncature dans les pages de résultats Google et renforce les intentions d'achat.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Longueur SERP idéale</Badge>
                  </div>
                </div>

                <div className="border border-border/80 rounded-xl p-3.5 bg-paper/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-clay-100 text-clay-800 text-xs font-mono font-bold">06</span>
                    <h4 className="text-xs font-bold text-ink">Méta Descriptions (120 - 160 car.)</h4>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">
                    Phrase percutante avec avantage produit et appel à l'action incitatif au clic (CTR), sans être tronquée par les moteurs sur les écrans mobiles.
                  </p>
                  <div className="pt-1">
                    <Badge tone="good">Taux de clic (CTR)</Badge>
                  </div>
                </div>
              </div>
            </PanelBody>
          </Panel>
        </div>
      )}

      {/* TAB 2, 3, 5: PRODUCT LIST WITH FILTERS */}
      {(activeTab === 'media' || activeTab === 'ean' || activeTab === 'content') && (
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card p-3.5 rounded-xl border border-border shadow-2xs">
            <div className="flex flex-1 items-center gap-2.5">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 text-ink-ghost absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Rechercher par nom, SKU, marque ou EAN..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 text-xs"
                />
              </div>

              <Select
                value={deficiencyFilter}
                onChange={(e) => {
                  setDeficiencyFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="text-xs w-56"
              >
                <option value="all">Tous les produits</option>
                <option value="missing_ean">Sans code-barres EAN-13</option>
                <option value="unconfirmed_ean">EAN non confirmé Couche 2</option>
                <option value="missing_images">Sans aucune photo</option>
                <option value="ready_shopping">Éligibles Google Shopping</option>
              </Select>

              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="text-xs w-44"
              >
                <option value="score_asc">Score SEO croissant (urgents)</option>
                <option value="score_desc">Score SEO décroissant</option>
                <option value="name_asc">Nom A → Z</option>
                <option value="id_desc">Derniers ajoutés</option>
              </Select>
            </div>

            <div className="text-xs font-mono text-ink-faint shrink-0">
              {isProductsFetching ? 'Chargement…' : `${productsData?.pagination.total ?? 0} produits`}
            </div>
          </div>

          {/* Products Table */}
          <TableShell>
            <Table>
              <THead>
                <Tr>
                  <Th className="w-14">Visuel</Th>
                  <Th>Désignation & SKU</Th>
                  <Th className="w-36">EAN-13 (GTIN)</Th>
                  <Th className="w-28 text-center">Score SEO</Th>
                  <Th className="w-48">Anomalies Détectées</Th>
                  <Th className="w-56">Texte Alternatif (Alt)</Th>
                  <Th className="w-24 text-right">Actions</Th>
                </Tr>
              </THead>
              <TBody>
                {isProductsLoading ? (
                  <Tr>
                    <Td colSpan={7} className="text-center py-10 text-xs text-ink-soft">
                      Chargement de l'audit SEO des produits…
                    </Td>
                  </Tr>
                ) : productsData?.items.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} className="text-center py-10 text-xs text-ink-soft">
                      Aucun produit ne correspond aux filtres appliqués.
                    </Td>
                  </Tr>
                ) : (
                  productsData?.items.map((p) => {
                    const firstImg = p.images[0];
                    return (
                      <Tr key={p.id}>
                        {/* Image Preview with 1000px resolution badge */}
                        <Td>
                          <div className="relative w-11 h-11 rounded-lg border border-border/80 overflow-hidden bg-canvas flex items-center justify-center shrink-0">
                            {firstImg ? (
                              <img
                                src={firstImg}
                                alt={p.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <span className="text-[9px] text-ink-ghost font-mono">0 img</span>
                            )}
                          </div>
                        </Td>

                        {/* Name & SKU */}
                        <Td>
                          <div className="space-y-0.5">
                            <div className="text-xs font-semibold text-ink leading-snug line-clamp-2">
                              {p.name}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] font-mono text-ink-soft">
                              <span>SKU: {p.sku ?? '—'}</span>
                              {p.brand && (
                                <>
                                  <span>·</span>
                                  <span className="text-herb-800 font-sans font-medium">{p.brand}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </Td>

                        {/* EAN-13 Status */}
                        <Td>
                          {p.gtinCanonical || p.barcodeRaw ? (
                            <div className="space-y-1">
                              <code className="text-xs font-mono font-semibold text-ink">
                                {p.gtinCanonical ?? p.barcodeRaw}
                              </code>
                              <div>
                                {p.finalEanConfirmed ? (
                                  <Badge tone="good">✓ Couche 2</Badge>
                                ) : (
                                  <Badge tone="signal">Non confirmé</Badge>
                                )}
                              </div>
                            </div>
                          ) : (
                            <Badge tone="alarm">EAN manquant</Badge>
                          )}
                        </Td>

                        {/* Score SEO */}
                        <Td className="text-center">
                          <span
                            className={cn(
                              'inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold font-mono',
                              p.seo.score >= 80
                                ? 'bg-emerald-100 text-emerald-800'
                                : p.seo.score >= 50
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-rose-100 text-rose-800',
                            )}
                          >
                            {p.seo.score} / 100
                          </span>
                        </Td>

                        {/* Anomalies */}
                        <Td>
                          <div className="flex flex-wrap gap-1">
                            {p.seo.anomalies.length === 0 ? (
                              <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Conforme
                              </span>
                            ) : (
                              p.seo.anomalies.slice(0, 3).map((a) => (
                                <span
                                  key={a}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-cream text-ink-soft border border-border/70"
                                >
                                  {a}
                                </span>
                              ))
                            )}
                          </div>
                        </Td>

                        {/* Suggested Alt text */}
                        <Td>
                          <div className="text-[11px] text-ink-soft line-clamp-2 italic leading-relaxed">
                            "{p.seo.suggestedAlt}"
                          </div>
                        </Td>

                        {/* Actions */}
                        <Td className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() => setSelectedProductForModal(p)}
                              title="Voir aperçu SERP et attributs médias"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() => {
                                setSelectedProductId(p.id);
                                setActiveTab('schema');
                              }}
                              title="Générer et tester le Schema JSON-LD"
                            >
                              <Code2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              disabled={pushAltsMutation.isPending}
                              onClick={() => handlePushSingleAlt(p)}
                              title="Pousser Alt vers WooCommerce"
                              className="text-emerald-700 hover:bg-emerald-50"
                            >
                              <UploadCloud className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </TBody>
            </Table>
          </TableShell>

          {/* Pagination */}
          {productsData && productsData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-xs font-mono text-ink-soft px-2">
              <div>
                Page {productsData.pagination.page} sur {productsData.pagination.totalPages}
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Précédent
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={currentPage >= productsData.pagination.totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: SCHEMA.ORG JSON-LD GENERATOR */}
      {activeTab === 'schema' && (
        <div className="space-y-6">
          <Panel>
            <PanelHeader
              index="SCHEMA"
              title="Générateur & Validateur Schema.org JSON-LD (Données Structurées)"
              hint="Balisage riche Product et Offer conforme à Google Search Central et Schema.org"
            />
            <PanelBody className="space-y-4">
              <p className="text-xs text-ink-soft leading-relaxed max-w-3xl">
                Google exige le balisage structuré <code>@type: "Product"</code> avec l'identifiant global <code>gtin13</code>, le prix en <code>TND</code> et la disponibilité pour activer les fiches enrichies (étoiles d'avis, prix et statut stock direct dans les résultats de recherche).
              </p>

              {/* Selector or Selected Product */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-cream/40 p-3.5 rounded-xl border border-border">
                <div className="space-y-0.5">
                  <div className="text-xs font-semibold text-ink">
                    {schemaData
                      ? `Données générées pour le produit #${schemaData.productId}`
                      : 'Sélectionnez un produit pour prévisualiser son Schema JSON-LD'}
                  </div>
                  <div className="text-[11px] text-ink-faint font-mono">
                    Valide syntaxiquement pour l'outil de test des résultats enrichis de Google
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopySchema}
                    disabled={!schemaData}
                  >
                    {copiedSchema ? (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                        <span>Copié !</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                        <span>Copier JSON-LD</span>
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open('https://search.google.com/test/rich-results', '_blank')
                    }
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    <span>Test Résultats Enrichis Google</span>
                  </Button>
                </div>
              </div>

              {/* JSON Code Viewer */}
              {isSchemaLoading ? (
                <div className="p-8 text-center text-xs text-ink-soft font-mono">
                  Génération du schéma JSON-LD…
                </div>
              ) : schemaData ? (
                <div className="relative">
                  <pre className="p-4 rounded-xl bg-canvas text-ink font-mono text-xs overflow-x-auto border border-border leading-relaxed max-h-[500px]">
                    {schemaData.jsonString}
                  </pre>
                </div>
              ) : (
                <div className="p-12 text-center border border-dashed border-border rounded-xl text-xs text-ink-soft space-y-2">
                  <Code2 className="w-6 h-6 text-ink-ghost mx-auto" />
                  <div>Veuillez sélectionner un produit dans l'onglet Photos ou EAN pour afficher son Schema.</div>
                </div>
              )}

              {/* Ready to use WordPress snippet */}
              <div className="mt-4 border-t border-border pt-4 space-y-2">
                <h4 className="text-xs font-bold text-ink">Snippet d'intégration WordPress (functions.php) :</h4>
                <pre className="p-3 bg-paper rounded-lg border border-border text-[11px] font-mono text-ink-soft overflow-x-auto">
{`add_action('wp_head', function() {
    if (!is_product()) return;
    global $product;
    if (!$product) return;
    $schema = [
        '@context' => 'https://schema.org/',
        '@type' => 'Product',
        'name' => $product->get_name(),
        'sku' => $product->get_sku(),
        'gtin13' => get_post_meta($product->get_id(), '_global_unique_id', true),
        'offers' => [
            '@type' => 'Offer',
            'priceCurrency' => 'TND',
            'price' => $product->get_price(),
            'availability' => $product->is_in_stock() ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        ]
    ];
    echo '<script type="application/ld+json">' . json_encode($schema, JSON_UNESCAPED_UNICODE) . '</script>';
});`}
                </pre>
              </div>
            </PanelBody>
          </Panel>
        </div>
      )}

      {/* DETAIL & SERP SIMULATOR MODAL */}
      {selectedProductForModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={() => setSelectedProductForModal(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card rounded-2xl border border-border shadow-2xl p-6 space-y-5 my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <h3 className="text-sm font-bold text-ink">Diagnostic SEO & Attributs Médias</h3>
                <p className="text-xs text-ink-soft">Produit #{selectedProductForModal.id} · SKU: {selectedProductForModal.sku}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedProductForModal(null)}
                className="text-xs font-mono text-ink-faint hover:text-ink px-2 py-1"
              >
                ✕ Fermer
              </button>
            </div>

            {/* SERP Preview Mode Switch */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-ink">Aperçu SERP Google :</span>
                <div className="flex items-center gap-1 bg-cream/70 p-1 rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => setSerpViewMode('desktop')}
                    className={cn(
                      'px-2 py-1 rounded text-xs font-semibold flex items-center gap-1',
                      serpViewMode === 'desktop' ? 'bg-ink text-canvas' : 'text-ink-soft',
                    )}
                  >
                    <Monitor className="w-3 h-3" /> Ordinateur
                  </button>
                  <button
                    type="button"
                    onClick={() => setSerpViewMode('mobile')}
                    className={cn(
                      'px-2 py-1 rounded text-xs font-semibold flex items-center gap-1',
                      serpViewMode === 'mobile' ? 'bg-ink text-canvas' : 'text-ink-soft',
                    )}
                  >
                    <Smartphone className="w-3 h-3" /> Mobile
                  </button>
                </div>
              </div>

              {/* SERP Box */}
              <div
                className={cn(
                  'p-4 bg-white rounded-xl border border-slate-200 font-sans shadow-sm',
                  serpViewMode === 'mobile' ? 'max-w-sm mx-auto' : 'w-full',
                )}
              >
                <div className="text-[11px] text-[#202124] flex items-center gap-1.5 truncate">
                  <span className="font-semibold text-[#202124]">maparatunisie.tn</span>
                  <span className="text-slate-400">›</span>
                  <span className="text-slate-500 truncate">produit › {selectedProductForModal.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}</span>
                </div>
                <div className="mt-1 text-sm text-[#1a0dab] font-medium hover:underline cursor-pointer truncate">
                  {selectedProductForModal.seo.suggestedTitle}
                </div>
                <div className="mt-1 text-xs text-[#4d5156] leading-relaxed line-clamp-2">
                  {selectedProductForModal.seo.suggestedMetaDesc}
                </div>
              </div>
            </div>

            {/* 4 Media Attributes Checklist */}
            <div className="space-y-2.5 bg-paper/60 p-3.5 rounded-xl border border-border">
              <h4 className="text-xs font-bold text-ink">Les 4 Attributs Médias WordPress Générés :</h4>
              <div className="space-y-1.5 text-xs">
                <div>
                  <span className="font-semibold text-ink mr-2">1. Texte Alternatif (Alt) :</span>
                  <span className="text-ink-soft font-mono">"{selectedProductForModal.seo.suggestedAlt}"</span>
                </div>
                <div>
                  <span className="font-semibold text-ink mr-2">2. Légende (Caption) :</span>
                  <span className="text-ink-soft font-mono">"{selectedProductForModal.seo.suggestedCaption}"</span>
                </div>
                <div>
                  <span className="font-semibold text-ink mr-2">3. Description Média :</span>
                  <span className="text-ink-soft font-mono">"{selectedProductForModal.seo.suggestedMetaDesc}"</span>
                </div>
                <div>
                  <span className="font-semibold text-ink mr-2">4. Titre Média Propre :</span>
                  <span className="text-ink-soft font-mono">"{selectedProductForModal.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.jpg"</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedProductForModal(null)}
              >
                Fermer
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={pushAltsMutation.isPending}
                onClick={() => {
                  handlePushSingleAlt(selectedProductForModal);
                  setSelectedProductForModal(null);
                }}
                className="bg-emerald-700 hover:bg-emerald-600 text-white font-semibold"
              >
                <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
                <span>Pousser Alt vers WooCommerce</span>
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
